// @ts-nocheck
import { useCallback } from "react";
import useSWR, { preload } from "swr";
import { FileProp } from "src/types/shared.types";
import { useAuth } from "../auth";
import { useContractState } from "src/contexts";
import { deleteContractApi, listContractsApi, updateContractApi } from "src/api";
import { sortContracts } from "src/utils/ContractUtils";
import { useOrg } from "src/contexts/OrgContext";

const fetcher = async (orgId?: string): Promise<FileProp[]> => {
  const response = await listContractsApi(orgId);
  if (!Array.isArray(response?.contracts)) return [];

  const processed = response.contracts.map((contract: any) => ({
    ...contract,
    sharedWith: contract.sharedUsers?.map((user: any) => user.userId) || [],
  }));

  return sortContracts(processed);
};

export const preloadContracts = (orgId?: string) => {
  // preload's promise becomes an unhandled rejection if the API 404s (e.g.,
  // a brand-new user with no contracts yet). Swallow here — useContractOperations
  // surfaces the same error via SWR's `error` field where the UI can render it.
  return preload(['contracts', orgId], () => fetcher(orgId)).catch(() => undefined);
};

/**
 * Single SWR source for contract data + CRUD operations.
 * No React elements in data — keeps contracts serializable for SWR cache.
 */
export const useContractOperations = () => {
  const { isAuthenticated } = useAuth();
  const { selectedOrgId } = useOrg();
  const {
    dispatchEditing,
    dispatchDelete,
  } = useContractState();

  const swrKey = isAuthenticated ? ['contracts', selectedOrgId] : null;

  const { data, error, isLoading, mutate } = useSWR<FileProp[]>(
    swrKey,
    () => fetcher(selectedOrgId)
  );

  const contracts = data ?? [];

  const refreshContracts = useCallback(async () => {
    await mutate();
  }, [mutate]);

  // --- Name editing ---

  const startEditingContractName = useCallback(
    (contractId: string, currentName: string) => {
      dispatchEditing({
        type: 'START_EDITING',
        payload: { id: contractId, name: currentName }
      });
    },
    [dispatchEditing]
  );

  const saveContractName = useCallback(
    async (contractId: string, newName: string) => {
      if (!isAuthenticated || !contractId || !newName.trim()) return;

      // Optimistic update via SWR
      await mutate(
        contracts.map(c =>
          c.contractId === contractId ? { ...c, name: newName } : c
        ),
        false
      );

      try {
        await updateContractApi(contractId, newName);
        await mutate();
      } catch (err) {
        await mutate();
        throw err;
      } finally {
        dispatchEditing({ type: 'SAVE_SUCCESS' });
      }
    },
    [isAuthenticated, contracts, mutate, dispatchEditing]
  );

  const cancelEditingName = useCallback(() => {
    dispatchEditing({ type: 'CANCEL' });
  }, [dispatchEditing]);

  // --- Deletion ---

  const initiateDeleteContract = useCallback(
    (contractId: string, contractName: string) => {
      dispatchDelete({
        type: 'SHOW_CONFIRMATION',
        payload: { id: contractId, name: contractName }
      });
    },
    [dispatchDelete]
  );

  const confirmDeleteContract = useCallback(
    async (contractId: string) => {
      if (!isAuthenticated || !contractId) return;

      await mutate(
        contracts.filter(c => c.contractId !== contractId),
        false
      );

      try {
        await deleteContractApi(contractId);
        await mutate();
      } catch (err) {
        await mutate();
        throw err;
      } finally {
        dispatchDelete({ type: 'HIDE_CONFIRMATION' });
      }
    },
    [isAuthenticated, contracts, mutate, dispatchDelete]
  );

  const cancelDeleteContract = useCallback(() => {
    dispatchDelete({ type: 'HIDE_CONFIRMATION' });
  }, [dispatchDelete]);

  const handleOptimisticUpdate = useCallback(() => {
    refreshContracts();
  }, [refreshContracts]);

  return {
    contracts,
    isLoading,
    error,
    mutate,
    refreshContracts,
    startEditingContractName,
    saveContractName,
    cancelEditingName,
    initiateDeleteContract,
    confirmDeleteContract,
    cancelDeleteContract,
    handleOptimisticUpdate,
  };
};
